<template>
    <tr v-if="hasAnyAvailability(availabilityType, dates, allotmentType, subAllotment)" :class="contentClass">
        <td @click="isAllotmentExpanded = !isAllotmentExpanded">
            <span v-if="allotmentType !== 'CUSTOMER'"> {{ $t(`allotmentType.${allotmentType}`) }} </span>
            <span v-else> {{ subAllotment.name }} </span>
            <v-icon :icon="`mdi-chevron-${isAllotmentExpanded ? 'up' : 'down'}`" />
        </td>
        <td v-for="day in dates" :key="`${day}`" class="text-center" :class="dateCellCSSClass(day)">
            <AvailabilityTableLabel
                :availability="gatAvailability(availabilityType, day, allotmentType, null, null, subAllotment)"
            />
        </td>
    </tr>
    <template v-if="isAllotmentExpanded">
        <template
            v-for="(basicRoomGroup, basicRoomIndex) in basicRoomGroups"
            :key="basicRoomGroup.basicRoomId || 'no-basic-room'"
        >
            <!-- Basic Room Row -->
            <tr :class="contentClass">
                <td class="ps-5" @click="isBasicRoomExpanded[basicRoomIndex] = !isBasicRoomExpanded[basicRoomIndex]">
                    <div class="text-no-wrap">
                        {{ basicRoomGroup.basicRoomName }}
                        <v-icon :icon="`mdi-chevron-${isBasicRoomExpanded[basicRoomIndex] ? 'up' : 'down'}`" />
                    </div>
                    <div
                        v-if="basicRoomGroup.basicRoom?.serviceId"
                        class="text-no-wrap text-caption text-grey-darken-1"
                    >
                        [ID: {{ basicRoomGroup.basicRoom.serviceId }}]
                    </div>
                </td>
                <td
                    v-for="day in dates"
                    :key="`${basicRoomGroup.basicRoomId}-${day}`"
                    :class="dateCellCSSClass(day)"
                    class="text-center"
                >
                    <AvailabilityTableLabel
                        :availability="
                            getBasicRoomAvailability(availabilityType, day, allotmentType, basicRoomGroup, subAllotment)
                        "
                    />
                </td>
            </tr>
            <template v-if="isBasicRoomExpanded[basicRoomIndex]">
                <!-- Sustainable Room Row -->
                <tr
                    v-if="basicRoomGroup.sustainableRoom"
                    :key="`sustainable-${basicRoomGroup.basicRoomId}`"
                    :class="contentClass"
                >
                    <td class="ps-10">
                        <div class="text-no-wrap">{{ basicRoomGroup.sustainableRoom.serviceName }}</div>
                        <div class="text-no-wrap text-caption text-grey">({{ $t('sustainableRoom') }})</div>
                        <div
                            v-if="basicRoomGroup.sustainableRoom?.serviceId"
                            class="text-no-wrap text-caption text-grey-darken-1"
                        >
                            [ID: {{ basicRoomGroup.sustainableRoom.serviceId }}]
                        </div>
                    </td>
                    <td
                        v-for="day in dates"
                        :key="`sustainable-${basicRoomGroup.basicRoomId}-${day}`"
                        :class="dateCellCSSClass(day)"
                        class="text-center"
                    >
                        <AvailabilityTableLabel
                            :availability="
                                gatAvailability(
                                    availabilityType,
                                    day,
                                    allotmentType,
                                    basicRoomGroup.sustainableRoom.categoryName,
                                    basicRoomGroup.sustainableRoom.serviceName,
                                    subAllotment
                                )
                            "
                        />
                    </td>
                </tr>
                <!-- Zero Room Row -->
                <tr v-if="basicRoomGroup.zeroRoom" :key="`zero-${basicRoomGroup.basicRoomId}`" :class="contentClass">
                    <td class="ps-10">
                        <div class="text-no-wrap">{{ basicRoomGroup.zeroRoom.serviceName }}</div>
                        <div class="text-no-wrap text-caption text-grey">({{ $t('zeroRoom') }})</div>
                        <div
                            v-if="basicRoomGroup.zeroRoom?.serviceId"
                            class="text-no-wrap text-caption text-grey-darken-1"
                        >
                            [ID: {{ basicRoomGroup.zeroRoom.serviceId }}]
                        </div>
                    </td>
                    <td
                        v-for="day in dates"
                        :key="`zero-${basicRoomGroup.basicRoomId}-${day}`"
                        :class="dateCellCSSClass(day)"
                        class="text-center"
                    >
                        <AvailabilityTableLabel
                            :availability="
                                gatAvailability(
                                    availabilityType,
                                    day,
                                    allotmentType,
                                    basicRoomGroup.zeroRoom.categoryName,
                                    basicRoomGroup.zeroRoom.serviceName,
                                    subAllotment
                                )
                            "
                        />
                    </td>
                </tr>
            </template>
        </template>
    </template>
</template>

<script setup lang="ts">
    import {gatAvailability, hasAnyAvailability} from '~src/utils/availability'
    import AvailabilityTableLabel from '~src/components/products/AvailabilityTableLabel.vue'

    const {
        allotmentType,
        availabilityType,
        contentClass,
        dates,
        contract,
        roomTypes: propRoomTypes,
        hotelServices: propHotelServices,
        roomTypesLoaded: propRoomTypesLoaded,
    } = defineProps<{
        allotmentType: 'NETTO' | 'FREE_SALE' | 'CUSTOMER'
        contentClass?: string
        availabilityType: any[]
        dates: Date[]
        subAllotment?: any
        contract?: any
        roomTypes?: any[]
        hotelServices?: any[]
        roomTypesLoaded?: boolean
    }>()

    const {$api} = useNuxtApp()

    // Use props if available, otherwise fall back to loading data
    const localRoomTypes = ref([])
    const localHotelServices = ref([])
    const localRoomTypesLoaded = ref(false)
    const localHotelServicesLoaded = ref(false)

    const roomTypes = computed(() => propRoomTypes || localRoomTypes.value)
    const hotelServices = computed(() => propHotelServices || localHotelServices.value)
    const roomTypesLoaded = computed(() =>
        propRoomTypesLoaded !== undefined ? propRoomTypesLoaded : localRoomTypesLoaded.value
    )
    const hotelServicesLoaded = computed(() => propHotelServices !== undefined || localHotelServicesLoaded.value)

    const isAllotmentExpanded = ref(false)
    const isBasicRoomExpanded = ref([])

    // Computed property for basic room groups that updates when room types change
    const basicRoomGroups = computed(() => {
        if (!roomTypesLoaded.value || !hotelServicesLoaded.value) return []
        return getBasicRoomGroups(availabilityType, allotmentType)
    })

    // Watch for changes in basic room groups to update expanded state
    watch(
        basicRoomGroups,
        newGroups => {
            isBasicRoomExpanded.value = newGroups.map(() => false)
        },
        {immediate: true}
    )

    // Load room types and hotel services when component mounts (only if not provided via props)
    onMounted(async () => {
        // Only load data if not provided via props
        if (!propRoomTypes || !propRoomTypesLoaded) {
            await loadRoomTypes()
        }
        if (!propHotelServices) {
            await loadHotelServices()
        }
    })

    async function loadRoomTypes() {
        if (localRoomTypesLoaded.value) return

        try {
            // Get hotel ID from contract or supplier store
            const hotelId = contract?.hotelId || contract?.supplierId
            if (!hotelId) {
                localRoomTypesLoaded.value = true
                return
            }

            const response = await $api.hotelDescriptionRoomType.get(hotelId, {locale: 'all'})
            localRoomTypes.value = response.hotelRoomTypes || []
            localRoomTypesLoaded.value = true
        } catch (error) {
            console.error('Error loading room types:', error)
            localRoomTypes.value = []
            localRoomTypesLoaded.value = true
        }
    }

    async function loadHotelServices() {
        if (localHotelServicesLoaded.value) return

        try {
            const response = await $api.ownHotelServices.get({contractId: contract?.id})
            localHotelServices.value = response.services || []
            localHotelServicesLoaded.value = true
        } catch (error) {
            console.error('Error loading hotel services:', error)
            localHotelServices.value = []
            localHotelServicesLoaded.value = true
        }
    }

    function getRoomTypeByServiceId(serviceId) {
        // First try to find the service in hotel services to get the room type ID
        const hotelService = hotelServices.value.find(service => service.id === serviceId)

        if (hotelService && hotelService.hotelRoomTypeId) {
            // Use the hotelRoomTypeId from hotel service to find the room type
            const roomType = roomTypes.value.find(rt => rt.id === hotelService.hotelRoomTypeId)
            if (roomType) {
                return roomType
            }
        }

        // Fallback: try direct serviceId match with room type ID
        const directMatch = roomTypes.value.find(rt => rt.id === serviceId)
        if (directMatch) {
            return directMatch
        }

        return null
    }

    function getBasicRoomGroups(availabilityType, allotmentType) {
        // Get all availability items for this allotment type
        const availabilityItems = availabilityType.filter(availability => availability.allotmentType === allotmentType)

        // Group by basic room ID using actual room type data
        const groupsMap = new Map()

        availabilityItems.forEach((item, index) => {
            // Try to find room type by serviceId using the proper hotel service mapping
            let roomType = null

            if (item.serviceId) {
                roomType = getRoomTypeByServiceId(item.serviceId)
            }

            // For same-name room types with different sustainability levels, we should NOT
            // fall back to name-based matching as it will incorrectly group different services
            // Only use name-based matching if we have no serviceId at all
            if (!roomType && !item.serviceId && item.serviceName) {
                roomType = roomTypes.value.find(
                    rt =>
                        rt.name?.en === item.serviceName ||
                        rt.roomCode === item.serviceName ||
                        Object.values(rt.name || {}).includes(item.serviceName)
                )
            }

            let basicRoomId, basicRoomName, sustainabilityLevel

            if (roomType) {
                // Use actual room type data
                sustainabilityLevel = roomType.sustainabilityLevel || 'BASIC'

                if (sustainabilityLevel === 'BASIC') {
                    basicRoomId = roomType.id
                    basicRoomName = roomType.name?.en || roomType.name || item.serviceName || item.categoryName
                } else {
                    // For sustainable/zero rooms, use the basicRoomId to group them
                    basicRoomId = roomType.basicRoomId || roomType.id
                    // Find the basic room name
                    const basicRoom = roomTypes.value.find(rt => rt.id === basicRoomId)
                    basicRoomName = basicRoom?.name?.en || basicRoom?.name || item.serviceName || item.categoryName
                }
            } else {
                // Fallback when room type data is not available
                // Use serviceId as unique identifier to avoid grouping different services
                const serviceName = item.serviceName || ''
                const categoryName = item.categoryName || ''
                const serviceId = item.serviceId

                const isSustainableRoom =
                    serviceName.toLowerCase().includes('sustainable') ||
                    serviceName.toLowerCase().includes('sustain') ||
                    categoryName.toLowerCase().includes('sustainable')

                const isZeroRoom =
                    serviceName.toLowerCase().includes('zero') || categoryName.toLowerCase().includes('zero')

                if (isSustainableRoom) {
                    sustainabilityLevel = 'SUSTAINABLE'
                } else if (isZeroRoom) {
                    sustainabilityLevel = 'ZERO'
                } else {
                    sustainabilityLevel = 'BASIC'
                }

                if (sustainabilityLevel === 'BASIC') {
                    // For basic rooms, use serviceId if available, otherwise use service name
                    basicRoomId = serviceId || serviceName || categoryName
                    basicRoomName = serviceName || categoryName
                } else {
                    // For sustainable/zero rooms, use serviceId to ensure unique grouping
                    // but try to extract base name for display
                    if (serviceId) {
                        // Use serviceId as unique identifier to prevent incorrect grouping
                        basicRoomId = `${serviceId}_base`
                        basicRoomName = serviceName || categoryName
                    } else {
                        // Extract base name for special rooms only if no serviceId
                        let baseServiceName = serviceName
                            .replace(/\s*(sustainable|sustain|zero)\s*/gi, '')
                            .replace(/\s+/g, ' ')
                            .trim()

                        if (!baseServiceName) {
                            baseServiceName = categoryName
                                .replace(/\s*(sustainable|sustain|zero)\s*/gi, '')
                                .replace(/\s+/g, ' ')
                                .trim()
                        }

                        basicRoomId = baseServiceName || serviceName
                        basicRoomName = baseServiceName || serviceName
                    }
                }
            }

            if (!groupsMap.has(basicRoomId)) {
                groupsMap.set(basicRoomId, {
                    basicRoomId,
                    basicRoomName,
                    basicRoom: null,
                    sustainableRoom: null,
                    zeroRoom: null,
                })
            }

            const group = groupsMap.get(basicRoomId)

            // Assign to appropriate room type based on sustainability level
            if (sustainabilityLevel === 'SUSTAINABLE') {
                group.sustainableRoom = item
            } else if (sustainabilityLevel === 'ZERO') {
                group.zeroRoom = item
            } else {
                group.basicRoom = item
            }
        })

        return Array.from(groupsMap.values()).filter(
            group => group.basicRoom || group.sustainableRoom || group.zeroRoom
        )
    }

    function getBasicRoomAvailability(availabilityType, day, allotmentType, basicRoomGroup, subAllotment) {
        // Return availability for the basic room in this group
        if (basicRoomGroup.basicRoom) {
            return gatAvailability(
                availabilityType,
                day,
                allotmentType,
                basicRoomGroup.basicRoom.categoryName,
                basicRoomGroup.basicRoom.serviceName,
                subAllotment
            )
        }
        return null
    }

    function hasAnyAvailability(availabilityType, dates, allotmentType, subAllotment = null) {
        return !!dates.find(date =>
            ['AVAILABLE', 'UNLIMITED', 'RELEASED', 'OVERBOOKING', 'BLOCKED', 'TERMINATED_ALLOTMENT'].includes(
                gatAvailability(availabilityType, date, allotmentType, null, null, subAllotment)?.status
            )
        )
    }

    function dateCellCSSClass(day) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const dayDate = new Date(day)
        dayDate.setHours(0, 0, 0, 0)

        if (dayDate.getTime() === today.getTime()) {
            return 'bg-blue-lighten-5'
        }
        return ''
    }
</script>
